import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

export default function ChatBox({ rideId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const lastTsRef = useRef(null);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const upsertMessage = useCallback((msg) => {
    if (!msg || !msg.message_id) return;
    if (seenIdsRef.current.has(msg.message_id)) return;
    seenIdsRef.current.add(msg.message_id);
    setMessages((prev) => [...prev, msg]);
    lastTsRef.current = msg.created_at;
  }, []);

  const fetchMessages = useCallback(async (after) => {
    try {
      const params = after ? { after } : {};
      const { data } = await api.get(`/rides/${rideId}/messages`, { params });
      if (data.length) {
        const fresh = data.filter((m) => !seenIdsRef.current.has(m.message_id));
        fresh.forEach((m) => seenIdsRef.current.add(m.message_id));
        if (fresh.length) {
          setMessages((prev) => [...prev, ...fresh]);
          lastTsRef.current = data[data.length - 1].created_at;
        }
      }
    } catch (err) {
      console.error("ChatBox fetch failed:", err);
    }
  }, [rideId]);

  // Initial fetch + WebSocket connect (with polling fallback)
  useEffect(() => {
    seenIdsRef.current = new Set();
    setMessages([]);
    fetchMessages();

    let ws = null;
    let pollTimer = null;
    let reconnectTimer = null;
    let closed = false;

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => fetchMessages(lastTsRef.current), 4000);
    };
    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const connect = async () => {
      if (closed) return;
      try {
        // Get short-lived ws token (HttpOnly cookie auth → JS-readable token)
        let wsToken = null;
        try {
          const { data } = await api.get("/auth/ws-token");
          wsToken = data?.token;
        } catch { /* will fall back to polling */ }
        if (!wsToken) { startPolling(); return; }
        const wsUrl = BACKEND_URL.replace(/^https?:/, BACKEND_URL.startsWith("https") ? "wss:" : "ws:")
          + `/api/rides/${rideId}/ws?token=${encodeURIComponent(wsToken)}`;
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => { stopPolling(); };
        ws.onmessage = (evt) => {
          try {
            const payload = JSON.parse(evt.data);
            if (payload?.type === "message" && payload.data) upsertMessage(payload.data);
          } catch { /* ignore malformed */ }
        };
        ws.onerror = () => { /* triggers onclose */ };
        ws.onclose = (e) => {
          if (closed) return;
          startPolling();
          if (e.code !== 4401 && e.code !== 4404) {
            reconnectTimer = setTimeout(connect, 5000);
          }
        };
      } catch {
        startPolling();
      }
    };

    connect();
    // Safety: always poll as a fallback so we never miss messages even if WS open is slow
    startPolling();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      stopPolling();
      if (ws) {
        try { ws.close(); } catch { /* noop */ }
      }
    };
  }, [rideId, fetchMessages, upsertMessage]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/rides/${rideId}/messages`, { text: text.trim() });
      upsertMessage(data);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="tactical-card rounded-sm flex flex-col h-[560px]">
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="label-tag">Chat del giro</div>
        <div className="font-display font-bold text-lg">Coordina con il gruppo</div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-sm text-gray-500 py-12">
            Nessun messaggio ancora. Rompi il ghiaccio.
          </div>
        )}
        {messages.map((m) => {
          const mine = user && m.user_id === user.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-2 rounded-sm ${mine ? "bg-[#E11D48] text-white" : "bg-gray-100 text-gray-900 border border-gray-200"}`}>
                {!mine && (
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-600 mb-1">
                    {m.user_name}
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-gray-500"}`}>
                  {format(new Date(m.created_at), "HH:mm · d MMM", { locale: it })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="border-t border-gray-200 p-3 flex gap-2">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={user ? "Scrivi un messaggio..." : "Accedi per scrivere"}
          disabled={!user || sending}
          className="flex-1 bg-gray-50 border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-3 py-2 text-sm text-gray-900"
        />
        <button
          data-testid="chat-send-btn"
          type="submit"
          disabled={!user || !text.trim() || sending}
          className="btn-primary px-4 rounded-sm flex items-center gap-2 disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
